/**
 * LeadFieldsAdmin.tsx — Admin > Lead Fields (the port of legacy
 * admin_lead_field_list.php).
 *
 * Laid out like every Admin list (Lead Sources is the reference — see
 * components/admin/AdminList.tsx), with the corp Lead Fields columns less
 * Accounts: Title, Field, Shortcode, each sorting from its title (by Title to
 * start). The corp page's only filter is Account,
 * so there is none here. "Add Lead Field" sits top right in the header; a
 * click on a row opens its edit form; Edit / Delete icons on the right (lead
 * fields are deleted outright, so there is no Show deleted); 25 rows a page.
 * On a Locked page (useAdminPageLevel) there is no Add, no row icon and no
 * Actions column; a row still opens, as a viewer with a single Close.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import { toast } from '@/store/toastStore';
import { fieldErrorsFromError } from './autoresponders/autoresponderForm';
import * as adminSvc from '@/services/admin.service';
import type { LeadField, LeadFieldInput } from '@/services/admin.service';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * The legacy page's shortcode rule (admin_lead_field_list.php): the Field text
 * with spaces, then every non-word character, removed.
 */
function shortcodeFromField(field: string): string {
  return field.replace(/\s/g, '').replace(/\W/g, '');
}

/**
 * The server's sentence when it answered, else our own. A request that never
 * got an answer carries only a transport message ("Network Error"), which is
 * not something a user can act on.
 */
function messageFrom(err: unknown, fallback: string): string {
  return axios.isAxiosError(err) && err.response ? getErrorMessage(err, fallback) : fallback;
}

function byTitle(a: LeadField, b: LeadField): number {
  return a.fieldTitle.localeCompare(b.fieldTitle, undefined, { sensitivity: 'base' });
}

// The corp Lead Fields columns, less Accounts.
const COLUMNS: AdminListColumn[] = [
  { key: 'title', header: 'Title', className: 'w-[30%]', sortable: true },
  { key: 'field', header: 'Field', sortable: true },
  { key: 'shortcode', header: 'Shortcode', sortable: true },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

/** Title is the list's own order; ties keep it. */
const SORT_COMPARE: ListSortCompare<LeadField> = {
  title: byTitle,
  field: (a, b) => compareText(a.fieldOptions, b.fieldOptions),
  shortcode: (a, b) => compareText(a.shortcode, b.shortcode),
};

// ─── Modal ───────────────────────────────────────────────────────────────────

interface LeadFieldModalProps {
  leadField?: LeadField | null;
  onClose: () => void;
  onSaved: (saved: LeadField) => void;
}

function LeadFieldModal({ leadField, onClose, onSaved }: LeadFieldModalProps) {
  const isEdit = leadField != null;
  // Locked: a viewer — fields read-only, Close only.
  const locked = useAdminPageLevel() === 'locked';

  const [form, setForm] = useState<LeadFieldInput>(() => ({
    fieldTitle: leadField?.fieldTitle ?? '',
    fieldOptions: leadField?.fieldOptions ?? '',
    shortcode: leadField?.shortcode ?? '',
  }));
  // A shortcode the admin typed (or one the field already had) is theirs;
  // only an empty one follows the Field text.
  const [shortcodeOwned, setShortcodeOwned] = useState(() => (leadField?.shortcode ?? '') !== '');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const canSave = form.fieldTitle.trim() !== '' && form.fieldOptions.trim() !== '';

  function setFieldText(value: string) {
    setForm((f) => ({
      ...f,
      fieldOptions: value,
      shortcode: shortcodeOwned ? f.shortcode : shortcodeFromField(value),
    }));
  }

  function setShortcode(value: string) {
    setShortcodeOwned(value !== '');
    setForm((f) => ({ ...f, shortcode: value }));
  }

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    if (!canSave || saving || locked) return;

    const payload: LeadFieldInput = {
      fieldTitle: form.fieldTitle.trim(),
      fieldOptions: form.fieldOptions.trim(),
      shortcode: form.shortcode.trim(),
    };

    setSaving(true);
    setError(null);
    setFieldErrors({});
    try {
      const saved = isEdit
        ? await adminSvc.updateLeadField(leadField.contactFieldId, payload)
        : await adminSvc.createLeadField(payload);
      toast.success(isEdit ? 'Lead field saved' : 'Lead field added');
      onSaved(saved);
      onClose();
    } catch (err) {
      setFieldErrors(fieldErrorsFromError(err));
      setError(messageFrom(err, 'The lead field was not saved. Try again.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={locked ? 'Lead Field' : isEdit ? 'Edit Lead Field' : 'Add Lead Field'}
      size="md"
      footer={
        locked ? (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={() => void handleSubmit()} disabled={saving || !canSave} loading={saving}>
              {isEdit ? 'Save Changes' : 'Add Lead Field'}
            </Button>
          </>
        )
      }
    >
      <AdminLockedFieldset>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}

          <FormField label="Title" required={!locked} htmlFor="lead-field-title" error={fieldErrors.fieldTitle}>
            <Input
              id="lead-field-title"
              value={form.fieldTitle}
              onChange={(e) => setForm((f) => ({ ...f, fieldTitle: e.target.value }))}
              placeholder="e.g. Primary Interest"
              maxLength={100}
              autoFocus
            />
          </FormField>

          <FormField
            label="Field"
            required={!locked}
            htmlFor="lead-field-field"
            error={fieldErrors.fieldOptions}
            helpText="The label as it appears in incoming lead emails and on forms."
          >
            <Input
              id="lead-field-field"
              value={form.fieldOptions}
              onChange={(e) => setFieldText(e.target.value)}
              placeholder="e.g. Primary Interest"
              maxLength={100}
            />
          </FormField>

          <FormField label="Shortcode" htmlFor="lead-field-shortcode" error={fieldErrors.shortcode}>
            <Input
              id="lead-field-shortcode"
              value={form.shortcode}
              onChange={(e) => setShortcode(e.target.value)}
              placeholder="e.g. PrimaryInterest"
              maxLength={100}
            />
          </FormField>
        </form>
      </AdminLockedFieldset>
    </Modal>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

export function LeadFieldsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [items, setItems] = useState<LeadField[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState<LeadField | null>(null);
  const [deleteItem, setDeleteItem] = useState<LeadField | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await adminSvc.listLeadFields());
      setLoadError(null);
    } catch (err) {
      setLoadError(messageFrom(err, 'Lead fields could not be loaded. Refresh the page to try again.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Put a saved field in place (or add it), so the row reflects the server. */
  function upsert(saved: LeadField) {
    setItems((list) =>
      list.some((f) => f.contactFieldId === saved.contactFieldId)
        ? list.map((f) => (f.contactFieldId === saved.contactFieldId ? saved : f))
        : [...list, saved],
    );
  }

  async function handleDelete() {
    if (!deleteItem) return;
    const target = deleteItem;
    try {
      await adminSvc.deleteLeadField(target.contactFieldId);
      setItems((list) => list.filter((f) => f.contactFieldId !== target.contactFieldId));
      toast.success('Lead field deleted');
    } catch (err) {
      toast.error(messageFrom(err, 'The lead field was not deleted. Try again.'));
    } finally {
      setDeleteItem(null);
    }
  }

  const query = searchQuery.trim().toLowerCase();
  const visibleItems = useMemo(() => {
    const matches = query
      ? items.filter((f) =>
          [f.fieldTitle, f.fieldOptions, f.shortcode].some((v) => v.toLowerCase().includes(query)),
        )
      : items;
    return [...matches].sort(byTitle);
  }, [items, query]);

  const sorting = useListSort(visibleItems, { initialKey: 'title', compare: SORT_COMPARE });

  // ── Pagination (back to page 1 when the search or the sort changes) ───
  const pagination = useListPagination(sorting.sorted, { paged: true, resetOn: [query, sorting.sort] });

  return (
    <AdminListPage
      title="Lead Fields"
      subtitle="Manage the details captured from incoming leads."
      actions={
        <Button onClick={() => setShowCreate(true)} leftIcon={<Plus className="h-4 w-4" />}>
          Add Lead Field
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search by title, field or shortcode…"
          searchLabel="Search lead fields"
        />
      }
    >
      {loadError ? (
        <p className="flex-shrink-0 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
          {loadError}
        </p>
      ) : loading ? (
        <SpinnerOverlay label="Loading lead fields…" />
      ) : visibleItems.length === 0 ? (
        <AdminListEmpty
          title={query ? 'No lead fields match your search' : 'No lead fields yet'}
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add a lead field to get started.'}
        />
      ) : (
        <>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="Lead fields" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((field) => (
              <tr
                key={field.contactFieldId}
                data-lead-field-id={field.contactFieldId}
                className={adminRowClass({ clickable: true })}
                onClick={() => setEditItem(field)}
              >
                <td className={ADMIN_TD_CLASS}>
                  <AdminRowName nameTestId="lead-field-title">{field.fieldTitle}</AdminRowName>
                </td>
                <td className={cn(ADMIN_TD_CLASS, 'truncate')}>{field.fieldOptions || '—'}</td>
                <td className={cn(ADMIN_TD_CLASS, 'truncate')}>{field.shortcode || '—'}</td>
                {!locked && (
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowActions>
                      <AdminRowAction
                        kind="edit"
                        title="Edit lead field"
                        label={`Edit ${field.fieldTitle}`}
                        onClick={() => setEditItem(field)}
                      />
                      <AdminRowAction
                        kind="delete"
                        label={`Delete ${field.fieldTitle}`}
                        onClick={() => setDeleteItem(field)}
                      />
                    </AdminRowActions>
                  </td>
                )}
              </tr>
            ))}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['lead field', 'lead fields']} />
        </>
      )}

      {showCreate && (
        <LeadFieldModal onClose={() => setShowCreate(false)} onSaved={upsert} />
      )}

      {editItem && (
        <LeadFieldModal leadField={editItem} onClose={() => setEditItem(null)} onSaved={upsert} />
      )}

      <ConfirmDialog
        isOpen={deleteItem !== null}
        title="Delete Lead Field"
        message={
          deleteItem
            ? `"${deleteItem.fieldTitle}" will be deleted. Leads you already received keep what they captured, but new leads will no longer fill this field.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteItem(null)}
      />
    </AdminListPage>
  );
}
